import { formatDate } from '@angular/common'
import { TableFilterMatching, TableFilterType } from './shared.types'
import * as moment from 'moment'
import { getArea } from 'ol/sphere'

export const booleanToWord = (value: boolean) => (value ? 'YES' : 'NO')

export const fileClass = (fineName: string): string => {
  const ext = fineName.substr(fineName.lastIndexOf('.') + 1)

  switch (ext) {
    case 'xlsx':
      return 'fa-file-excel'
    case 'pdf':
      return 'fa-file-pdf'
    case 'csv':
      return 'fa-file-csv'
    case 'txt':
      return 'fa-file-alt'
    case 'docs':
      return 'fa-file-word'
    case 'png':
      return 'fa-file-image'
    case 'jpg':
      return 'fa-file-image'
    case 'jpeg':
      return 'fa-file-image'
    default:
      return 'fa-file-alt'
  }
}

export const downloadBlob = (response: any) => {
  if (
    !response ||
    !response.headers ||
    !response.headers.get('content-disposition')
  )
    return
  const blob: Blob = response.body
  const filename: string = response.headers
    .get('content-disposition')
    .split(';')[1]
    .split('filename')[1]
    .split('=')[1]
    .replace(/['"]+/g, '')
    .trim()
  const navigator = window.navigator as any

  if (navigator && navigator.msSaveOrOpenBlob) {
    navigator.msSaveOrOpenBlob(blob, filename)
  } else {
    const exportUrl = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = exportUrl
    link.download = filename
    document.body.appendChild(link)
    link.dispatchEvent(
      new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window,
      }),
    )
    link.remove()
    window.URL.revokeObjectURL(link.href)
  }
}

export function getCookie(cname: string): string {
  let name = cname + '='
  let decodedCookie = decodeURIComponent(document.cookie)
  let ca = decodedCookie.split(';')
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i]
    while (c.charAt(0) == ' ') {
      c = c.substring(1)
    }
    if (c.indexOf(name) == 0) {
      return c.substring(name.length, c.length)
    }
  }
  return ''
}

export function getRowValidations(data: any, attrNames: any = {}) {
  const errors = {}

  Object.keys(data).forEach((key) => {
    if (key.includes('.')) {
      const newKey = key.split('.').slice(1).join('.')
      const newVal = data[key].map((v: string) => {
        const fieldName = key
          .split('.')
          .slice(key.split('.').length - 1)
          .join('')

        return v
          .replace(key, fieldName)
          .replace(fieldName, attrNames[fieldName] ?? fieldName)
      })

      errors[newKey] = newVal
    }
  })

  return errors
}

export function filtersToDbQuery(data: any) {
  const dbQuery = {}

  data.forEach(
    ({
      operator,
      field,
      value,
      range_from,
      range_to,
      matching,
      type,
      fieldKeys,
    }) => {
      let actualOperator: string
      let actualValue: any
      let start: any
      let end: any

      if (matching === TableFilterMatching.Includes) {
        actualOperator = operator
      }

      if (
        matching === TableFilterMatching.Is ||
        matching === TableFilterMatching.Equals
      ) {
        actualOperator = `match${operator === 'or' ? 'Or' : 'And'}`
      }

      if (
        matching === TableFilterMatching.IsNot ||
        matching === TableFilterMatching.NotEquals
      ) {
        actualOperator = `notMatch${operator === 'or' ? 'Or' : 'And'}`
      }

      if (matching === TableFilterMatching.IsEmpty) {
        actualValue = ''
        actualOperator = `null${operator === 'or' ? 'Or' : 'And'}`
      } else if (matching === TableFilterMatching.IsNotEmpty) {
        actualValue = ''
        actualOperator = `notNull${operator === 'or' ? 'Or' : 'And'}`
      } else if (
        matching === TableFilterMatching.GreaterThanOrEqualTo ||
        matching === TableFilterMatching.LessThanOrEqualTo
      ) {
        actualValue = undefined
        if (matching === TableFilterMatching.GreaterThanOrEqualTo) {
          start = parseFloat(value)
        }

        if (matching === TableFilterMatching.LessThanOrEqualTo) {
          end = parseFloat(value)
        }
        actualOperator = `ranges`
      } else if (type === TableFilterType.Date) {
        actualValue = undefined
        start = range_from ? toDate(range_from) : undefined
        end = range_to ? toDate(range_to) : undefined
        actualOperator = `ranges`
      } else {
        actualValue =
          type === TableFilterType.Boolean
            ? value === 'true' || value === true
            : type === TableFilterType.String
            ? value
            : parseFloat(value)
      }

      const isEmptyMatching =
        matching === TableFilterMatching.IsEmpty ||
        matching === TableFilterMatching.IsNotEmpty

      if (actualOperator) {
        if (dbQuery[actualOperator]) {
          dbQuery[actualOperator] = isEmptyMatching
            ? [...dbQuery[actualOperator], field]
            : [
                ...dbQuery[actualOperator],
                ...(fieldKeys?.length
                  ? fieldKeys.map((key: string) => ({
                      field: `${field}.${key}`,
                      value: actualValue,
                      start,
                      end,
                      type: type === TableFilterType.Date ? 'DATE' : undefined,
                    }))
                  : [
                      {
                        field,
                        value: actualValue,
                        start,
                        end,
                        type:
                          type === TableFilterType.Date ? 'DATE' : undefined,
                      },
                    ]),
              ]
        } else {
          dbQuery[actualOperator] = isEmptyMatching
            ? [field]
            : [
                ...(fieldKeys?.length
                  ? fieldKeys.map((key: string) => ({
                      field: `${field}.${key}`,
                      value: actualValue,
                      start,
                      end,
                      type: type === TableFilterType.Date ? 'DATE' : undefined,
                    }))
                  : [
                      {
                        field,
                        value: actualValue,
                        start,
                        end,
                        type:
                          type === TableFilterType.Date ? 'DATE' : undefined,
                      },
                    ]),
              ]
        }
      }
    },
  )

  return dbQuery
}

export const detectDateFormat = (dateString: string) => {
  const formats = [
    { regex: /\d{4}-\d{2}-\d{2}/, format: 'YYYY-MM-DD' },
    { regex: /\d{2}-\d{2}-\d{4}/, format: 'DD-MM-YYYY' },
    { regex: /\d{2}\/\d{2}\/\d{4}/, format: 'DD/MM/YYYY' },
    { regex: /\d{4}\/\d{2}\/\d{2}/, format: 'YYYY/MM/DD' },
    { regex: /\d{2}\.\d{2}\.\d{4}/, format: 'DD.MM.YYYY' },
    { regex: /\d{4}\.\d{2}\.\d{2}/, format: 'YYYY.MM.DD' },
    { regex: /\d{2}\s[A-Za-z]+\s\d{4}/, format: 'DD Month YYYY' },
    { regex: /[A-Za-z]+\s\d{1,2},\s\d{4}/, format: 'Month DD, YYYY' },
  ]

  for (const formatObj of formats) {
    if (formatObj.regex.test(dateString)) {
      return { match: true, format: formatObj.format }
    }
  }

  return { match: false, format: null }
}

export function toDate(value: string) {
  const detected = detectDateFormat(value)

  if (detected.format) {
    if (detected.format.toLocaleLowerCase() === 'dd-mm-yyyy') {
      return value
    }
  }

  return value ? moment(value, 'yyyy-MM-DD').format('DD-MM-yyyy') : undefined
}

export function toDateTime(value: string) {
  return value
    ? moment(value, 'yyyy-MM-DD HH:mm:ss').format('DD-MM-yyyy HH:mm:ss')
    : undefined
}

export function getCenterCoordinatesFromMultiPolygon(coordinates) {
  let totalLat = 0
  let totalLng = 0
  let totalPoints = 0

  for (const polygon of coordinates) {
    for (const ring of polygon) {
      for (const point of ring) {
        totalLat += point[1] // Latitude
        totalLng += point[0] // Longitude
        totalPoints++
      }
    }
  }

  const avgLat = totalLat / totalPoints
  const avgLng = totalLng / totalPoints

  return [avgLng, avgLat] // Return [longitude, latitude]
}

export function fileToBase64(file: File) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve((reader.result as string).split(',')[1])
    reader.onerror = (error) => reject(error)
  })
}

export const roundLngLat = (value: any) => {
  const MAX_LONG_LAT_DECIMAL = 9

  return parseFloat(value.toFixed(MAX_LONG_LAT_DECIMAL))
}

export const sortCalculationFieldsByIndex = (arr: any[]) => {
  const result = new Array(arr.length) // Create an array of the same length

  // First, place items with the index in their correct positions
  arr.forEach((item) => {
    if (item.index !== undefined) {
      result[item.index] = item // Place it at the specified index
    }
  })

  // Now, fill in the remaining spots with items without an index
  let insertPos = 0
  arr.forEach((item) => {
    if (item.index === undefined) {
      // Find the next available spot in the result array
      while (result[insertPos] !== undefined) {
        insertPos++
      }
      result[insertPos] = item // Insert at the next available spot
    }
  })

  return result
}

export const groupPermissions = (permissions: any[]) =>
  Object.values(
    permissions.reduce((acc, permission) => {
      const groupName = permission.group?.name ?? 'OTHER'
      const translationKey = permission.group?.translationKey ?? 'other'
      const title = permission.group?.title ?? 'Other'

      if (!acc[groupName]) {
        acc[groupName] = {
          name: groupName,
          permissions: [],
          translationKey,
          title,
        }
      }
      acc[groupName].permissions.push(permission)
      return acc
    }, {}),
  )

export const colorGradient = (
  value: number,
  alpha: number,
  color1: { red: number; green: number; blue: number } = {
    red: 255,
    green: 0,
    blue: 0,
  },
  color2: { red: number; green: number; blue: number } = {
    red: 255,
    green: 255,
    blue: 0,
  },
  color3: { red: number; green: number; blue: number } = {
    red: 19,
    green: 233,
    blue: 19,
  },
): string => {
  let actualValue = value

  if (value > 1) actualValue = 1
  if (value == 0) actualValue = 0.1

  let startColor, endColor, ratio

  if (value <= 0.5) {
    startColor = color1
    endColor = color2
    ratio = value * 2 // Scale value to [0, 1] for the first gradient
  } else {
    startColor = color2
    endColor = color3
    ratio = (value - 0.5) * 2 // Scale value to [0, 1] for the second gradient
  }

  const red = Math.round(
    startColor.red + ratio * (endColor.red - startColor.red),
  )
  const green = Math.round(
    startColor.green + ratio * (endColor.green - startColor.green),
  )
  const blue = Math.round(
    startColor.blue + ratio * (endColor.blue - startColor.blue),
  )

  return `rgba(${red}, ${green}, ${blue}, ${alpha})`
}

export const calculateArea = (geometry: any, settings: any = {}): number => {
  const { SETTINGS_UNITS_AREA_ROUNDING, SETTINGS_UNITS_AREA_TO_METERS } =
    settings
  const multiplicator = Math.pow(10, SETTINGS_UNITS_AREA_ROUNDING)
  const area = getArea(geometry, { projection: 'EPSG:4326' })
  const result =
    Math.floor(area * SETTINGS_UNITS_AREA_TO_METERS * multiplicator) /
    multiplicator

  return result
}

export const areCoordinatesEqual = (coords1: any, coords2: any): boolean => {
  if (!coords1 || !coords2) return false
  if (coords1.length !== coords2.length) return false

  // Deep comparison for coordinate arrays
  return JSON.stringify(coords1) === JSON.stringify(coords2)
}

export const addUniqueCoordinates = (
  existingCoordinates: any[],
  newCoordinates: any[],
): void => {
  // Check each new coordinate set to see if it already exists
  newCoordinates.forEach((newCoord) => {
    const isDuplicate = existingCoordinates.some((existingCoord) =>
      areCoordinatesEqual(existingCoord, newCoord),
    )

    if (!isDuplicate) {
      existingCoordinates.push(newCoord)
    } else {
      console.log('Skipping duplicate coordinates:', newCoord)
    }
  })
}
